import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, format, isToday, isTomorrow, parseISO, startOfMonth, subMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import api from '../services/api';
import type { OrderResponse, PaginatedResponse } from '../hooks/useOrders';
import { Page } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Icon, type IconName } from '../components/ui/Icon';

type TrendDirection = 'up' | 'down' | 'warn';

type ActivityItem = {
  timestamp: string;
  text: string;
  icon: IconName;
  dot: 'ok' | 'info' | 'warn';
  path: string;
};

type MonthlySpendPoint = {
  label: string;
  amount: number;
  ship: number;
};

type InFlightOrder = {
  id: number;
  orderNumber: string;
  from: string;
  to: string;
  carrier: string;
  progress: number;
  eta: string;
  status: string;
  stage: string;
};

const fetchOverviewOrders = async (): Promise<PaginatedResponse<OrderResponse>> => {
  const response = await api.get('/orders', {
    params: {
      page: 1,
      limit: 60,
      sort_date: 'desc',
    },
  });

  if (!response.data?.meta) {
    const data = response.data?.data || [];
    return {
      data,
      meta: {
        current_page: 1,
        from: data.length ? 1 : 0,
        last_page: 1,
        per_page: data.length,
        to: data.length,
        total: data.length,
      },
    };
  }

  return response.data;
};

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 5) return 'Up late';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const currency = (value: number): string =>
  new Intl.NumberFormat('en-IL', {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  }).format(Number.isFinite(value) ? value : 0);

const percent = (value: number, compact = false): string => {
  const abs = Math.abs(value);
  if (compact) return `${value > 0 ? '+' : value < 0 ? '-' : ''}${abs.toFixed(abs >= 10 ? 0 : 1)}%`;
  return `${Math.round(value)}%`;
};

const deltaPill = (value: string, dir: TrendDirection): React.ReactNode => (
  <span className={`ov-delta ${dir}`}>{value}</span>
);

const monthKey = (date: Date): string => format(date, 'yyyy-MM');

const asDate = (value?: string | null): Date | null => {
  if (!value) return null;

  try {
    const parsed = parseISO(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  } catch {
    return null;
  }
};

const titleCaseStatus = (value?: string): string =>
  (value || 'pending').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const extractPlace = (address: any): string => {
  if (!address) return 'Unknown';
  const city = address.city?.name || address.city_other || address.city || '';
  const country = address.country?.name || address.country || '';
  if (city && country) return `${city}`;
  return city || country || address.contactName || address.name || 'Unknown';
};

const getShipmentStatus = (order: OrderResponse): string => {
  if (order.shipment_status) return order.shipment_status;
  switch (order.status) {
    case 'shipped':
      return 'in_transit';
    case 'delivered':
      return 'delivered';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'pending';
  }
};

const isInMotion = (status: string): boolean =>
  ['ready', 'scheduled_for_pickup', 'in_transit'].includes(status);

const isActiveShipment = (status: string): boolean =>
  ['ready', 'scheduled_for_pickup', 'in_transit', 'out_for_delivery', 'exception'].includes(status);

const shipmentProgress = (status: string): number => {
  switch (status) {
    case 'ready':
      return 0.2;
    case 'scheduled_for_pickup':
      return 0.35;
    case 'in_transit':
      return 0.68;
    case 'out_for_delivery':
      return 0.9;
    case 'exception':
      return 0.55;
    case 'delivered':
      return 1;
    default:
      return 0.12;
  }
};

const stageCopy = (order: OrderResponse, status: string): string => {
  switch (status) {
    case 'ready':
      return 'Ready for dispatch';
    case 'scheduled_for_pickup':
      return 'Pickup scheduled';
    case 'in_transit':
      return order.carrier ? `In transit with ${order.carrier}` : 'Linehaul in progress';
    case 'out_for_delivery':
      return 'With courier';
    case 'exception':
      return 'Needs attention';
    case 'delivered':
      return 'Delivered';
    default:
      return 'Awaiting update';
  }
};

const etaLabel = (order: OrderResponse): string => {
  const etaDate = asDate(order.delivery_estimate);
  if (etaDate) {
    if (isToday(etaDate)) return 'Today';
    if (isTomorrow(etaDate)) return 'Tomorrow';
    return format(etaDate, 'MMM d');
  }
  return order.delivery_estimate || 'TBD';
};

const savingsForOrder = (order: OrderResponse): number => {
  const selectedRate = order.selected_rate as any;
  const basePrice = Number(
    selectedRate?.base_price ??
    selectedRate?.list_rate ??
    selectedRate?.retail_rate ??
    order.total_price
  );
  const total = Number(order.total_price || 0);
  return Math.max(basePrice - total, 0);
};

const expectedDeliveryDate = (order: OrderResponse): Date | null => {
  const parsedEstimate = asDate(order.delivery_estimate);
  if (parsedEstimate) return parsedEstimate;

  const rawEstimate = order.delivery_estimate || '';
  const dayMatch = rawEstimate.match(/(\d+)/);
  const createdAt = asDate(order.created_at);
  if (dayMatch && createdAt) {
    return addDays(createdAt, Number(dayMatch[1]));
  }

  const selectedRate = order.selected_rate as any;
  const fallbackDays = Number(selectedRate?.delivery_days ?? selectedRate?.deliveryDays);
  if (!Number.isNaN(fallbackDays) && createdAt) {
    return addDays(createdAt, fallbackDays);
  }

  return null;
};

const relativeTime = (value: string): string => {
  const date = asDate(value);
  if (!date) return 'Recently';

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  if (hours < 48) return 'Yesterday';

  const days = Math.round(hours / 24);
  if (days < 7) return `${days} days ago`;

  return format(date, 'MMM d');
};

const carrierColor = (carrier: string): string => {
  switch (carrier.toLowerCase()) {
    case 'aramex':
      return '#e4002b';
    case 'dhl':
      return '#ffcc00';
    case 'fedex':
      return '#4d148c';
    case 'ups':
      return '#6b4f2a';
    case 'smsa':
      return '#00a1e4';
    default:
      return '#8b94a8';
  }
};

const buildMonthlySpend = (orders: OrderResponse[]): MonthlySpendPoint[] => {
  const buckets = new Map<string, MonthlySpendPoint>();

  for (let offset = 5; offset >= 0; offset--) {
    const date = subMonths(startOfMonth(new Date()), offset);
    buckets.set(monthKey(date), {
      label: format(date, 'MMM'),
      amount: 0,
      ship: 0,
    });
  }

  orders.forEach((order) => {
    const createdAt = asDate(order.created_at);
    if (!createdAt) return;
    const key = monthKey(createdAt);
    const bucket = buckets.get(key);
    if (!bucket) return;
    bucket.ship += 1;
    if (order.payment_status === 'paid' || order.status === 'delivered' || order.status === 'shipped') {
      bucket.amount += Number(order.total_price || 0);
    }
  });

  return Array.from(buckets.values());
};

const Sparkline: React.FC<{ points: number[]; color: string }> = ({ points, color }) => {
  const values = points.length ? points : [0, 0, 0, 0, 0, 0];
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const width = 120;
  const height = 36;
  const plot = values
    .map((value, index) => {
      const x = values.length === 1 ? width : (index / (values.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');

  const lastPoint = values[values.length - 1] || 0;
  const lastY = height - ((lastPoint - min) / range) * height;
  const gradientId = `spark-${color.replace(/[^a-z0-9]/gi, '')}-${values.length}`;

  return (
    <svg className="ov-spark" viewBox={`0 0 ${width} ${height + 4}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${plot} ${width},${height}`} fill={`url(#${gradientId})`} />
      <polyline points={plot} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={width} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
};

const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { balance, transactions } = useWallet();
  const overviewQuery = useQuery({
    queryKey: ['overview-orders'],
    queryFn: fetchOverviewOrders,
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const orders = overviewQuery.data?.data || [];
  const currentMonthStart = startOfMonth(new Date());
  const previousMonthStart = startOfMonth(subMonths(new Date(), 1));
  const previousMonthKey = monthKey(previousMonthStart);
  const monthlySpend = buildMonthlySpend(orders);

  const shipmentsThisMonth = orders.filter((order) => {
    const createdAt = asDate(order.created_at);
    return createdAt ? createdAt >= currentMonthStart : false;
  }).length;

  const shipmentsLastMonth = orders.filter((order) => {
    const createdAt = asDate(order.created_at);
    return createdAt ? monthKey(createdAt) === previousMonthKey : false;
  }).length;

  const shipmentsDelta = shipmentsLastMonth
    ? ((shipmentsThisMonth - shipmentsLastMonth) / shipmentsLastMonth) * 100
    : shipmentsThisMonth > 0 ? 100 : 0;

  const currentMonthSavings = orders
    .filter((order) => {
      const createdAt = asDate(order.created_at);
      return createdAt ? createdAt >= currentMonthStart : false;
    })
    .reduce((sum, order) => sum + savingsForOrder(order), 0);

  const previousMonthSavings = orders
    .filter((order) => {
      const createdAt = asDate(order.created_at);
      return createdAt ? monthKey(createdAt) === previousMonthKey : false;
    })
    .reduce((sum, order) => sum + savingsForOrder(order), 0);

  const savingsDelta = previousMonthSavings
    ? ((currentMonthSavings - previousMonthSavings) / previousMonthSavings) * 100
    : currentMonthSavings > 0 ? 100 : 0;

  const deliveredOrders = orders.filter((order) => getShipmentStatus(order) === 'delivered');
  const onTimeDelivered = deliveredOrders.filter((order) => {
    const deliveredAt = asDate(order.delivered_at);
    const expectedAt = expectedDeliveryDate(order);
    return deliveredAt && expectedAt ? deliveredAt <= expectedAt : false;
  }).length;
  const onTimeRate = deliveredOrders.length ? (onTimeDelivered / deliveredOrders.length) * 100 : 0;

  const inMotionCount = orders.filter((order) => isInMotion(getShipmentStatus(order))).length;
  const outForDeliveryCount = orders.filter((order) => getShipmentStatus(order) === 'out_for_delivery').length;

  const activeOrders: InFlightOrder[] = orders
    .filter((order) => isActiveShipment(getShipmentStatus(order)))
    .sort((a, b) => {
      const bTime = asDate(b.updated_at)?.getTime() || 0;
      const aTime = asDate(a.updated_at)?.getTime() || 0;
      return bTime - aTime;
    })
    .slice(0, 3)
    .map((order) => {
      const status = getShipmentStatus(order);
      return {
        id: order.id,
        orderNumber: order.order_number,
        from: extractPlace(order.from_address),
        to: extractPlace(order.to_address),
        carrier: order.carrier || order.selected_rate?.provider || 'Carrier pending',
        progress: shipmentProgress(status),
        eta: etaLabel(order),
        status: titleCaseStatus(status),
        stage: stageCopy(order, status),
      };
    });

  const spendTotal = monthlySpend.reduce((sum, point) => sum + point.amount, 0);
  const spendAverage = monthlySpend.length ? spendTotal / monthlySpend.length : 0;
  const savedVsList = orders.reduce((sum, order) => sum + savingsForOrder(order), 0);
  const currentSpend = monthlySpend.slice(-3).reduce((sum, point) => sum + point.amount, 0);
  const previousSpend = monthlySpend.slice(0, 3).reduce((sum, point) => sum + point.amount, 0);
  const spendDelta = previousSpend ? ((currentSpend - previousSpend) / previousSpend) * 100 : currentSpend > 0 ? 100 : 0;

  const walletRunwayDays = spendAverage > 0 ? Math.round(balance / Math.max(spendAverage / 30, 1)) : null;

  const carrierRows = Object.entries(
    orders.reduce<Record<string, number>>((acc, order) => {
      const carrier = order.carrier || order.selected_rate?.provider || 'Other';
      acc[carrier] = (acc[carrier] || 0) + 1;
      return acc;
    }, {})
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const carrierTotal = carrierRows.reduce((sum, [, count]) => sum + count, 0) || 1;

  const activities: ActivityItem[] = [
    ...orders.slice(0, 5).map((order): ActivityItem => {
      const status = getShipmentStatus(order);
      const icon: IconName =
        status === 'delivered'
          ? 'checkCircle'
          : status === 'exception'
            ? 'alert'
            : status === 'out_for_delivery' || status === 'in_transit'
              ? 'truck'
              : 'package';
      const dot: ActivityItem['dot'] =
        status === 'delivered' ? 'ok' : status === 'exception' ? 'warn' : 'info';
      const routeText =
        status === 'delivered'
          ? `Order #${order.order_number} was delivered`
          : status === 'exception'
            ? `Order #${order.order_number} has a delivery issue`
            : status === 'out_for_delivery'
              ? `Order #${order.order_number} is out for delivery`
              : status === 'in_transit'
                ? `Order #${order.order_number} is moving with ${order.carrier || 'the carrier'}`
                : `Order #${order.order_number} was created`;

      return {
        timestamp: order.updated_at || order.created_at,
        text: routeText,
        icon,
        dot,
        path: `/orders/${order.id}`,
      };
    }),
    ...transactions.slice(0, 3).map((transaction): ActivityItem => ({
      timestamp: transaction.date,
      text:
        transaction.type === 'deposit'
          ? `Wallet topped up · ${currency(transaction.amount)}`
          : transaction.description || `Wallet ${transaction.type}`,
      icon: transaction.type === 'deposit' ? 'wallet' : 'receipt',
      dot: transaction.type === 'deposit' ? 'ok' : 'info',
      path: '/wallet',
    })),
  ]
    .sort((a, b) => {
      const bTime = asDate(b.timestamp)?.getTime() || 0;
      const aTime = asDate(a.timestamp)?.getTime() || 0;
      return bTime - aTime;
    })
    .slice(0, 6);

  const heroStats = [
    {
      label: 'Shipments this month',
      value: `${shipmentsThisMonth}`,
      delta: percent(shipmentsDelta, true),
      deltaDir: shipmentsDelta >= 0 ? 'up' : 'down',
      sub: `vs. ${shipmentsLastMonth} last month`,
      spark: monthlySpend.map((point) => point.ship),
      color: 'var(--brand-blue)',
    },
    {
      label: 'Savings vs. list rates',
      value: currency(currentMonthSavings),
      delta: percent(savingsDelta, true),
      deltaDir: savingsDelta >= 0 ? 'up' : 'down',
      sub: 'saved this month',
      spark: monthlySpend.map((point) => point.amount * 0.08 + point.ship * 10),
      color: 'var(--ok)',
    },
    {
      label: 'On-time delivery',
      value: percent(onTimeRate),
      delta: `${onTimeDelivered}/${deliveredOrders.length || 0}`,
      deltaDir: onTimeRate >= 90 ? 'up' : 'warn',
      sub: 'delivered on or before ETA',
      spark: monthlySpend.map((_, index) => {
        const base = onTimeRate || 92;
        return Math.max(base - 4 + index * 0.8, 70);
      }),
      color: 'var(--brand-cyan)',
    },
    {
      label: 'Wallet balance',
      value: currency(balance),
      delta: walletRunwayDays !== null && walletRunwayDays < 14 ? 'Low' : 'Healthy',
      deltaDir: walletRunwayDays !== null && walletRunwayDays < 14 ? 'warn' : 'up',
      sub: walletRunwayDays !== null ? `≈ ${walletRunwayDays} days at current rate` : 'ready for your next shipment',
      spark: monthlySpend.map((point, index) => Math.max(balance - point.amount * (index + 1) * 0.18, 0)),
      color: 'var(--brand-amber)',
    },
  ] as const;

  const quickActions = [
    { icon: 'sparkle' as const, title: 'Paste an invoice', sub: 'Let AI fill the form', path: '/ai-shipment', accent: true, badge: 'NEW' },
    { icon: 'package' as const, title: 'Create shipment', sub: 'Compare carriers live', path: '/shipping' },
    { icon: 'route' as const, title: 'Track a parcel', sub: 'Any carrier, any number', path: '/track' },
    { icon: 'warehouse' as const, title: 'Internal transfer', sub: 'DC to DC shipments', path: '/internal-shipments/new' },
    { icon: 'wallet' as const, title: 'Top up wallet', sub: `Balance ${currency(balance)}`, path: '/wallet' },
    { icon: 'users' as const, title: 'Add contact', sub: 'Save addresses for reuse', path: '/contacts' },
  ];

  if (overviewQuery.isLoading && !overviewQuery.data) {
    return (
      <Page width="wide">
        <Card pad="lg">
          <div className="row gap-3" style={{ justifyContent: 'space-between' }}>
            <div className="stack gap-2">
              <div className="kicker">Overview</div>
              <h1 className="page-title">Loading your dashboard…</h1>
              <p className="page-sub">Pulling shipments, wallet activity, and recent movements.</p>
            </div>
            <Icon name="refresh" size={18} />
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page width="wide">
      <div className="ov">
        <section className="ov-hero">
          <div>
            <div className="kicker">{format(new Date(), 'EEEE · MMMM d')}</div>
            <h1 className="ov-greeting">
              {getGreeting()}, <span className="ov-name">{user?.first_name || 'there'}</span>.
            </h1>
            <p className="ov-sub">
              {inMotionCount > 0 || outForDeliveryCount > 0 ? (
                <>
                  <strong style={{ color: 'var(--ink)' }}>{inMotionCount} shipments</strong> are on the move, and
                  <strong style={{ color: 'var(--ink)' }}> {outForDeliveryCount} {outForDeliveryCount === 1 ? 'is' : 'are'} out for delivery</strong> today.
                </>
              ) : (
                <>
                  Your board is clear right now. Launch a new shipment or track an existing parcel to see live activity here.
                </>
              )}
            </p>
          </div>
          <div className="ov-hero-actions">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate('/ai-shipment')}
            >
              <Icon name="sparkle" size={15} />
              <span>Paste an invoice</span>
            </Button>
            <Button
              variant="blue"
              size="lg"
              onClick={() => navigate('/shipping')}
            >
              <Icon name="plus" size={15} />
              <span>New shipment</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => overviewQuery.refetch()}
              title="Refresh"
              disabled={overviewQuery.isFetching}
            >
              <Icon name="refresh" size={15} />
            </Button>
          </div>
        </section>

        {overviewQuery.isError ? (
          <Card pad="lg">
            <div className="row gap-3" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div className="stack gap-1">
                <strong>Couldn&apos;t load the overview.</strong>
                <span className="muted">Try refreshing the page or fetching the dashboard data again.</span>
              </div>
              <Button onClick={() => overviewQuery.refetch()}>
                <Icon name="refresh" size={14} />
                <span>Retry</span>
              </Button>
            </div>
          </Card>
        ) : null}

        <section className="ov-stats">
          {heroStats.map((stat) => (
            <div key={stat.label} className="ov-stat">
              <div className="ov-stat-label">{stat.label}</div>
              <div className="ov-stat-row">
                <div className="ov-stat-value">{stat.value}</div>
                <Sparkline points={stat.spark} color={stat.color} />
              </div>
              <div className="ov-stat-foot">
                {deltaPill(stat.delta, stat.deltaDir)}
                <span className="muted" style={{ fontSize: 12 }}>{stat.sub}</span>
              </div>
            </div>
          ))}
        </section>

        <section className="ov-grid">
          <Card className="ov-inflight">
            <div className="ov-card-head">
              <div>
                <div className="kicker">Live</div>
                <div className="ov-card-title">In flight</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
                <span>View all</span>
                <Icon name="arrowRight" size={13} />
              </Button>
            </div>

            <div className="ov-inflight-body">
              <div className="ov-map">
                <WorldMap shipments={activeOrders} />
              </div>

              <div className="ov-inflight-list">
                {activeOrders.length ? activeOrders.map((shipment) => (
                  <button
                    key={shipment.id}
                    type="button"
                    className="ov-flight"
                    onClick={() => navigate(`/orders/${shipment.id}`)}
                  >
                    <div className="ov-flight-top">
                      <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{shipment.orderNumber}</span>
                      <Badge tone="info" dot>{shipment.status}</Badge>
                    </div>
                    <div className="ov-flight-route">
                      <strong>{shipment.from}</strong>
                      <div className="ov-route-line">
                        <span className="ov-route-fill" style={{ width: `${shipment.progress * 100}%` }} />
                        <span className="ov-route-dot" style={{ left: `${shipment.progress * 100}%` }} />
                      </div>
                      <strong>{shipment.to}</strong>
                    </div>
                    <div className="ov-flight-foot">
                      <span className="muted">{shipment.stage}</span>
                      <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}>ETA {shipment.eta}</span>
                    </div>
                  </button>
                )) : (
                  <div className="ov-flight">
                    <div className="muted">No active shipments right now.</div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="ov-spend">
            <div className="ov-card-head">
              <div>
                <div className="kicker">Finance</div>
                <div className="ov-card-title">Shipping spend</div>
              </div>
              <Badge tone="navy">Last 6 months</Badge>
            </div>

            <div className="ov-spend-stats">
              <div>
                <div className="muted" style={{ fontSize: 12 }}>Total (6m)</div>
                <div className="ov-spend-big mono">{currency(spendTotal)}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12 }}>Avg / month</div>
                <div className="ov-spend-mid mono">{currency(spendAverage)}</div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 12 }}>Saved vs. list</div>
                <div className="ov-spend-mid mono" style={{ color: 'var(--ok)' }}>{currency(savedVsList)}</div>
              </div>
            </div>

            <div className="ov-bars">
              {monthlySpend.map((point, index) => {
                const max = Math.max(...monthlySpend.map((entry) => entry.amount), 1);
                const height = (point.amount / max) * 100;
                const current = index === monthlySpend.length - 1;

                return (
                  <div key={point.label} className={`ov-bar-col ${current ? 'current' : ''}`}>
                    <div className="ov-bar-tip mono">{currency(point.amount)}</div>
                    <div className="ov-bar-wrap">
                      <div className="ov-bar" style={{ height: `${height}%` }} />
                    </div>
                    <div className="ov-bar-label">{point.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="ov-spend-foot">
              <div className="ov-legend">
                <span className="ov-dot" style={{ background: 'var(--brand-blue)' }} />
                Monthly shipping cost
              </div>
              <span className="muted" style={{ fontSize: 12 }}>
                <strong style={{ color: spendDelta >= 0 ? 'var(--ok)' : 'var(--err)' }}>
                  {spendDelta >= 0 ? '↑' : '↓'} {Math.abs(spendDelta).toFixed(Math.abs(spendDelta) >= 10 ? 0 : 1)}%
                </strong> vs. the prior window
              </span>
            </div>
          </Card>
        </section>

        <section className="ov-grid-2">
          <Card className="ov-quick">
            <div className="ov-card-head">
              <div>
                <div className="kicker">Shortcuts</div>
                <div className="ov-card-title">What to do next</div>
              </div>
            </div>
            <div className="ov-quick-grid">
              {quickActions.map((action) => (
                <button
                  key={action.path}
                  type="button"
                  className={`ov-quick-item ${action.accent ? 'accent' : ''}`}
                  onClick={() => navigate(action.path)}
                >
                  <div className="ov-quick-icon"><Icon name={action.icon} size={16} /></div>
                  <div className="ov-quick-text">
                    <div className="ov-quick-title">
                      {action.title}
                      {action.badge ? (
                        <Badge tone="cyan" style={{ fontSize: 9, marginInlineStart: 6 }}>{action.badge}</Badge>
                      ) : null}
                    </div>
                    <div className="ov-quick-sub">{action.sub}</div>
                  </div>
                  <Icon name="arrowRight" size={13} color="var(--ink-4)" />
                </button>
              ))}
            </div>
          </Card>

          <Card className="ov-activity">
            <div className="ov-card-head">
              <div>
                <div className="kicker">Live feed</div>
                <div className="ov-card-title">Recent activity</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
                <Icon name="bell" size={13} />
              </Button>
            </div>
            <div className="ov-activity-list">
              {activities.length ? activities.map((activity, index) => (
                <button
                  key={`${activity.timestamp}-${index}`}
                  type="button"
                  className="ov-activity-item"
                  onClick={() => navigate(activity.path)}
                  style={{ background: 'transparent', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
                >
                  <div className={`ov-activity-dot dot-${activity.dot}`}><Icon name={activity.icon} size={12} /></div>
                  <div className="ov-activity-text">{activity.text}</div>
                  <div className="ov-activity-time">{relativeTime(activity.timestamp)}</div>
                </button>
              )) : (
                <div className="ov-activity-item">
                  <div className="ov-activity-dot dot-info"><Icon name="info" size={12} /></div>
                  <div className="ov-activity-text">Your recent shipments and wallet activity will appear here.</div>
                  <div className="ov-activity-time">Soon</div>
                </div>
              )}
            </div>
          </Card>

          <Card className="ov-carriers">
            <div className="ov-card-head">
              <div>
                <div className="kicker">Mix · 30 days</div>
                <div className="ov-card-title">Carrier split</div>
              </div>
            </div>
            <div className="ov-carrier-bar">
              {carrierRows.map(([carrier, count]) => (
                <div key={carrier} style={{ width: `${(count / carrierTotal) * 100}%`, background: carrierColor(carrier) }} title={`${carrier} · ${count}`} />
              ))}
            </div>
            <div className="ov-carrier-list">
              {carrierRows.length ? carrierRows.map(([carrier, count]) => (
                <div key={carrier} className="ov-carrier-row">
                  <span className="ov-dot" style={{ background: carrierColor(carrier) }} />
                  <span className="ov-carrier-name mono">{carrier}</span>
                  <span className="ov-carrier-pct">{Math.round((count / carrierTotal) * 100)}%</span>
                  <span className="ov-carrier-vol muted">{count}</span>
                </div>
              )) : (
                <div className="ov-carrier-row">
                  <span className="ov-dot" style={{ background: 'var(--ink-4)' }} />
                  <span className="ov-carrier-name mono">No shipments yet</span>
                  <span className="ov-carrier-pct">0%</span>
                  <span className="ov-carrier-vol muted">0</span>
                </div>
              )}
            </div>
          </Card>
        </section>

        <section className="ov-news">
          <div className="ov-whatsnew">
            <div className="ov-wn-text">
              <Badge tone="cyan" style={{ marginBottom: 10 }}>What&apos;s new · v2.4</Badge>
              <h3>AI shipment assistant now reads your Gmail.</h3>
              <p>Connect your inbox once and let Almaship detect new orders, draft labels, and queue them for approval. No more copy-paste.</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <Button variant="secondary" size="sm" onClick={() => navigate('/ai-shipment')} style={{ background: 'var(--brand-cyan)', color: 'var(--brand-navy)', borderColor: 'transparent' }}>
                  <span>Try it now</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/faq')} style={{ color: '#cfd1e4' }}>
                  <span>Learn more</span>
                </Button>
              </div>
            </div>
            <div className="ov-wn-viz" aria-hidden>
              <div className="ov-wn-inbox">
                <div className="ov-wn-msg">
                  <div className="ov-wn-avatar">SH</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>Order #2491 · Shopify</div>
                    <div style={{ fontSize: 11, color: '#9aa0c0' }}>2 items · Ship to Riyadh</div>
                  </div>
                </div>
                <div className="ov-wn-arrow">
                  <Icon name="sparkle" size={13} color="#19c6e6" />
                </div>
                <div className="ov-wn-label">
                  <div style={{ fontSize: 10, color: '#9aa0c0', marginBottom: 4 }}>LABEL READY</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>Aramex Priority</div>
                  <div className="mono" style={{ fontSize: 10, color: '#19c6e6', marginTop: 2 }}>$31.30</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Page>
  );
};

const WorldMap: React.FC<{ shipments: InFlightOrder[] }> = ({ shipments }) => {
  const points = [
    { from: { x: 330, y: 112 }, to: { x: 248, y: 76 } },
    { from: { x: 310, y: 118 }, to: { x: 302, y: 102 } },
    { from: { x: 340, y: 124 }, to: { x: 412, y: 156 } },
  ];

  if (!shipments.length) {
    return (
      <div className="ph" style={{ width: '100%', height: '100%' }}>
        Live route map
      </div>
    );
  }

  return (
    <svg viewBox="0 0 480 200" className="ov-world" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="dot" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.7" fill="var(--line-strong)" />
        </pattern>
        <linearGradient id="arc" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--brand-cyan)" />
          <stop offset="100%" stopColor="var(--brand-blue)" />
        </linearGradient>
      </defs>
      <g fill="url(#dot)">
        <path d="M30 60 Q40 40 80 42 Q130 44 150 70 Q160 90 140 110 Q110 130 80 128 Q40 122 30 100 Z" />
        <path d="M110 130 Q130 140 140 160 Q140 185 120 190 Q100 188 92 170 Q90 150 110 130 Z" />
        <path d="M220 50 Q250 40 280 46 Q300 60 296 80 Q286 98 276 106 Q272 118 280 138 Q284 160 264 176 Q240 184 220 170 Q208 150 216 126 Q222 110 214 92 Q208 70 220 50 Z" />
        <path d="M300 40 Q360 38 420 58 Q452 74 448 100 Q440 126 400 128 Q360 130 340 120 Q318 110 308 90 Q300 68 300 40 Z" />
        <path d="M400 140 Q430 136 448 150 Q452 168 430 174 Q404 170 400 154 Z" />
      </g>

      {shipments.map((shipment, index) => {
        const point = points[index % points.length];
        const midX = (point.from.x + point.to.x) / 2;
        const midY = Math.min(point.from.y, point.to.y) - 40;
        const pathId = `ship-path-${index}`;

        return (
          <g key={shipment.id}>
            <path
              id={pathId}
              d={`M${point.from.x} ${point.from.y} Q ${midX} ${midY} ${point.to.x} ${point.to.y}`}
              fill="none"
              stroke="url(#arc)"
              strokeWidth="1.5"
              strokeDasharray="3 3"
              opacity="0.9"
            />
            <circle cx={midX} cy={midY + 16} r="3" fill="var(--brand-cyan)" />

            <circle cx={point.from.x} cy={point.from.y} r="6" fill="var(--brand-blue)" opacity="0.18" />
            <circle cx={point.from.x} cy={point.from.y} r="3" fill="var(--brand-navy)" />
            <circle cx={point.to.x} cy={point.to.y} r="6" fill="var(--brand-cyan)" opacity="0.18" />
            <circle cx={point.to.x} cy={point.to.y} r="3" fill="var(--brand-blue)" />

            <text x={point.from.x + 7} y={point.from.y + 3} fontSize="8" fill="var(--ink-2)" fontFamily="var(--font-mono)">
              {shipment.from.split(',')[0]}
            </text>
            <text x={point.to.x + 7} y={point.to.y + 3} fontSize="8" fill="var(--ink-2)" fontFamily="var(--font-mono)">
              {shipment.to.split(',')[0]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default OverviewPage;
